'use client';

import { format } from 'date-fns-modern';
import { tz } from '@date-fns/tz';
import { useParams, useRouter } from 'next/navigation';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import type { StrapiCarrier, StrapiCityDatum } from '@/apis/strapi';
import type { ScheduleProduct } from '@/apis/tds';
import { deleteCart } from '@/apis/tds';
import { buildScheduleMetaData, getTicketCart } from '@/app/builders';
import { doTranslation } from '@/app/i18n/client';
import ButtonTertiary from '@/components/global/buttons/button-tertiary';
import ToastError from '@/components/global/toasts/toast-error';
import Loader from '@/components/loaders/loader';
import ScheduleCards from '@/components/schedule-results/schedule-cards';
import styles from '@/components/schedule-results/schedules.module.scss';
import { useCustomerDetail } from '@/context/customer-detail-context';
import { useLogin } from '@/context/login-context';
import type { ItineraryItem, Passenger, TicketCartGTM, TripDetailsForm } from '@/context/trip-search-context';
import { useTripSearch } from '@/context/trip-search-context';
import type { CarrierImage } from '@/utils/carriers';
import { errorMessageHelper } from '@/utils/errorMessageHelper';
import { localeSpecificPath } from '@/utils/localeUtils';
import { getFares, orderBy, getCarriers } from '@/utils/schedules';

type Props = {
  carrierImages: { [key: string]: CarrierImage };
  carrierInfo: { [key: string]: StrapiCarrier };
  earliestSchedule: ScheduleProduct | null;
  lowestPrice: number | null;
  outgoingSchedData: ScheduleProduct[];
  returnDate: Date | null;
  returnSchedData: ScheduleProduct[] | null;
  shortestTravelTime: number | null;
  trailwaysWebstoreAppURL?: string;
  originCityData: StrapiCityDatum | null;
  destinationCityData: StrapiCityDatum | null;
  travelDate: Date | null;
  errorMessage: string | null;
};

const Schedules = (props: Props): React.JSX.Element => {
  const { lng }: any = useParams();
  const router = useRouter();

  const { t } = doTranslation(lng);
  const {
    getAvailability,
    itinerary,
    scheduleResults,
    setItinerary,
    setScheduleResults,
    setLoaderProps,
    loaderProps,
    setTicketCartGTM,
    setTripDetailsForm,
    tripDetailsForm,
    isTripSelected,
    setIsTripSelected,
    withLoader
  } = useTripSearch();
  const { loginInfo, setLoginInfo, setUserCheckedOut } = useLogin();

  const ref = useRef<HTMLDivElement>(null);
  const [outboundSchedules, setOutboundSchedules] = useState(props.outgoingSchedData);
  const [returnSchedules, setReturnSchedules] = useState<ScheduleProduct[] | null>(null);
  const [isSelected, setIsSelected] = useState(false);
  const [departTimeSelected, setDepartTimeSelected] = useState<null | string>(null);
  const [arriveTimeSelected, setArriveTimeSelected] = useState<null | string>(null);
  const [fareSelected, setFareSelected] = useState<null | string>(null);
  const [scheduleUuidSelected, setScheduleUuidSelected] = useState<null | string>(null);
  const [scheduleMetaData, setScheduleMetaData] = useState(buildScheduleMetaData(null));
  const [error, setError] = useState<{ [key: string]: string } | null>(null);

  const { customer } = useCustomerDetail();

  // We want this effect to run only once when component mounts, not on back/forward navigation
  // Deleting the cart is expensive and prevents bfcache restoration
  useEffect(() => {
    setLoaderProps({
      isInitializedFrom: '',
      showLoader: false
    });

    // Add event listener to support bfcache restoration
    // Reference: https://developer.mozilla.org/en-US/docs/Glossary/bfcache
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        // Page was restored from bfcache
        setLoaderProps({
          isInitializedFrom: 'bfcache',
          showLoader: false
        });
      }
    });

    // Only delete cart if not coming from bfcache
    if (!document.startViewTransition) {
      deleteCart();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.destinationCityData && props.originCityData) {
      setUserCheckedOut(false);

      let timeZone = 'America/New_York';
      if (props.outgoingSchedData.length > 0) {
        timeZone = props.outgoingSchedData[0].scheduleRun?.origin?.timeZone ?? 'America/New_York';
      }

      const data: TripDetailsForm = {
        ada: false,
        adults: 1,
        children: 0,
        departDate: props.travelDate ? format(new Date(props.travelDate), 'yyyy-MM-dd', { in: tz(timeZone) }) : '',
        destination: props.destinationCityData?.attributes ?? undefined,
        isRoundTrip: props.returnDate !== null,
        origin: props.originCityData?.attributes,
        passengers: {
          adults: [
            {
              firstName: '',
              lastName: '',
              wheel: false,
              type: 'Adult'
            }
          ],
          children: [],
          seniors: []
        },
        returnDate: props.returnDate ? format(new Date(props.returnDate), 'yyyy-MM-dd', { in: tz(timeZone) }) : '',
        seniors: 0,
        shouldRefresh: false,
        webTransactionId: uuidv4(),
        wheelchairs: 0
      };
      setTripDetailsForm(data);

      setScheduleResults({
        destinationCityData: props.destinationCityData,
        originCityData: props.originCityData,
        outboundSchedules: buildValidSchedules(props.outgoingSchedData),
        returnSchedules: props.returnSchedData ? buildValidSchedules(props.returnSchedData) : []
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  useEffect(() => {
    setOutboundSchedules(props.outgoingSchedData);
    setFareSelected(null);
    setIsSelected(false);
    setReturnSchedules(null);
    ref.current?.classList.remove('return');
  }, [props.outgoingSchedData]);

  const handleSelect = (
    id: string,
    originUuid: string,
    destinationUuid: string,
    fare: string,
    departTime: string,
    arriveTime: string,
    trueNumber: string
  ) => {
    const outboundSchedules = props.outgoingSchedData.filter((schedule: ScheduleProduct) => {
      return (
        schedule.scheduleRun.scheduleUuid === id &&
        schedule.scheduleRun.origin.stopUuid === originUuid &&
        schedule.scheduleRun.destination.stopUuid === destinationUuid
      );
    });
    setOutboundSchedules(orderBy(outboundSchedules, 'departure time'));
    setFareSelected(fare);
    setDepartTimeSelected(departTime);
    setArriveTimeSelected(arriveTime);
    setScheduleUuidSelected(id);
    setIsSelected(true);
    const outboundRouteType = outboundSchedules[0]?.routeType || '';
    const outboundCarriers = getCarriers(outboundSchedules[0]).map((carrier) => carrier.code);
    const isFLIXOutbound = outboundRouteType === 'FLIX'; // GLI/Flix can be identified by routeType
    const isPPBOutbound = outboundCarriers.includes('PPB'); // .. but PPB needs to be identified by carrier code
    if (props.returnSchedData) {
      const returnSchedules = props.returnSchedData.filter((schedule: ScheduleProduct) => {
        const { origin, destination } = schedule.scheduleRun;
        const returnCarriers = getCarriers(schedule).map((carrier) => carrier.code);
        const isFLIXReturn = schedule.routeType === 'FLIX';
        const isPPBReturn = returnCarriers.includes('PPB');

        // GLI/FLIX and PPB are not compatible carriers for round trips
        if ((isFLIXOutbound && isPPBReturn) || (isPPBOutbound && isFLIXReturn)) {
          return false;
        }
        if (
          origin.stopUuid === outboundSchedules[0].scheduleRun.destination.stopUuid &&
          destination.stopUuid === outboundSchedules[0].scheduleRun.origin.stopUuid
        ) {
          return true;
        }
        return false;
      });
      setReturnSchedules(orderBy(returnSchedules, 'departure time'));
      setScheduleMetaData(buildScheduleMetaData(returnSchedules));
    }
    ref.current?.classList.add('return');
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleReset = () => {
    setOutboundSchedules(props.outgoingSchedData);
    setFareSelected(null);
    setIsSelected(false);
    setReturnSchedules(null);
    setFareSelected(null);
    setDepartTimeSelected(null);
    setArriveTimeSelected(null);
    setScheduleUuidSelected(null);
    ref.current?.classList.remove('return');
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const buildValidSchedules = (schedules: ScheduleProduct[]) => {
    return schedules.filter((schedule) => {
      const fares = getFares(schedule);
      return !fareSelected
        ? fares.saver.length || fares.flex.length
        : fareSelected === 'saver'
        ? fares.saver.length
        : fares.flex.length;
    });
  };

  const handleOrderBy = (column: string, type: string | undefined) => {
    let schedulesArray = type === 'outbound' ? outboundSchedules : returnSchedules;
    if (schedulesArray?.length) {
      schedulesArray = orderBy(schedulesArray, column);
      if (type === 'outbound') {
        setOutboundSchedules([...schedulesArray]);
      } else {
        setReturnSchedules([...schedulesArray]);
      }
    }
  };

  const navigateFromSchedules = async (
    type: string,
    uuid: string,
    originUuid: string,
    destinationUuid: string,
    trip: string,
    tripType: string
  ) => {
    setLoaderProps({
      showLoader: true,
      isInitializedFrom: tripType === 'round trip' && trip === 'outbound' ? '/schedules' : ''
    });

    if (!tripDetailsForm || !scheduleResults) {
      setLoaderProps({ showLoader: false, isInitializedFrom: '' });

      return;
    }
    const schedules = trip === 'outbound' ? outboundSchedules : returnSchedules;
    const schedule = schedules?.filter((schedule) => {
      return (
        schedule.scheduleRun.scheduleUuid === uuid &&
        schedule.scheduleRun.origin.stopUuid === originUuid &&
        schedule.scheduleRun.destination.stopUuid === destinationUuid
      );
    })[0];
    if (!schedule) {
      setLoaderProps({ showLoader: false, isInitializedFrom: '' });
      return;
    }
    const fares = getFares(schedule);
    const newItinerary: ItineraryItem = {
      ...schedule,
      fare: type === 'saver' ? fares.saver[0] : fares.flex[0],
      isReturnTrip: false,
      type
    };
    if (newItinerary.railgunFares) {
      if (type === 'saver') {
        newItinerary.railgunFares = { ...newItinerary.railgunFares, saver: fares.saver[0] ?? undefined };
      }
      if (type === 'flex') {
        newItinerary.railgunFares = { ...newItinerary.railgunFares, flex: fares.flex[0] ?? undefined };
      }
    }
    setIsTripSelected(true);
    setItinerary({
      ...itinerary,
      outbound: newItinerary,
      returning: undefined
    });

    if (trip === 'return') {
      setIsTripSelected(true);
      newItinerary.isReturnTrip = true;
    }

    const updateItinerary = () => {
      if (newItinerary.isReturnTrip) {
        if (itinerary?.outbound) {
          const value = {
            ...itinerary,
            returning: newItinerary
          };
          setItinerary(value);
          return value;
        }
      } else {
        if (itinerary?.returning) {
          delete itinerary.returning;
        }
        const value = {
          ...itinerary,
          outbound: newItinerary
        };
        setItinerary(value);
        return value;
      }
    };

    const updatedItinerary = updateItinerary();

    const passengers: Passenger[] = Object.values(tripDetailsForm.passengers).reduce(
      (arr: Passenger[], passengerList: Passenger[]) => {
        arr.push(...passengerList);
        return arr;
      },
      []
    );

    const ticketCart: TicketCartGTM = getTicketCart(itinerary, loginInfo, newItinerary, passengers, tripDetailsForm);

    setTicketCartGTM(ticketCart);

    if (tripType === 'round trip' && trip !== 'return') {
      try {
        await withLoader(getAvailability)(updatedItinerary);
      } catch (error: any) {
        setError(errorMessageHelper(error));
        handleReset();
      }
      return;
    }

    if (customer) {
      if (!loginInfo) {
        setLoginInfo(customer);
      }
      router.push(localeSpecificPath('/purchase/passengers', lng));
    } else {
      setLoginInfo(null);
      router.push(localeSpecificPath('/purchase/login', lng));
    }
  };

  return (
    <>
      {loaderProps.showLoader && <Loader isInitializedFrom={loaderProps.isInitializedFrom} />}
      {error && (
        <ToastError
          title={t('Error')}
          description={error.error}
          onClose={() => {
            setError(null);
          }}
        />
      )}
      <div className={styles.departure} id="cards" ref={ref}>
        <ScheduleCards
          arriveTimeSelected={arriveTimeSelected}
          carrierImages={props.carrierImages}
          carrierInfo={props.carrierInfo}
          departTimeSelected={departTimeSelected}
          destinationCityData={props.destinationCityData}
          earliestSchedule={props.earliestSchedule}
          errorMessage={props.errorMessage}
          fareSelected={fareSelected}
          handleOrderBy={handleOrderBy}
          handleSelect={props.returnDate ? handleSelect : null}
          isSchedules={true}
          isSelected={isSelected}
          lowestPrice={props.lowestPrice}
          navigateFromSchedules={navigateFromSchedules}
          originCityData={props.originCityData}
          returnDate={props.returnDate}
          schedules={buildValidSchedules(outboundSchedules)}
          scheduleUuidSelected={scheduleUuidSelected}
          shortestTravelTime={props.shortestTravelTime}
          trailwaysWebstoreAppURL={props.trailwaysWebstoreAppURL}
          travelDate={props.travelDate}
          trip={'outbound'}
          type={props.returnDate ? 'round trip' : 'one way'}
        />
        {isSelected && (
          <ButtonTertiary dark onClick={() => handleReset()}>
            <span className={styles.tertiary}>{t('Change Departure Trip')}</span>
          </ButtonTertiary>
        )}
      </div>
      {returnSchedules && (
        <div className={styles.return}>
          <ScheduleCards
            arriveTimeSelected={arriveTimeSelected}
            carrierImages={props.carrierImages}
            carrierInfo={props.carrierInfo}
            departTimeSelected={departTimeSelected}
            destinationCityData={props.destinationCityData}
            earliestSchedule={scheduleMetaData.earliestSchedule}
            fareSelected={fareSelected}
            handleOrderBy={handleOrderBy}
            isSchedules={true}
            lowestPrice={scheduleMetaData.lowestPrice}
            navigateFromSchedules={navigateFromSchedules}
            originCityData={props.originCityData}
            returnDate={props.returnDate}
            schedules={buildValidSchedules(returnSchedules)}
            scheduleUuidSelected={scheduleUuidSelected}
            shortestTravelTime={scheduleMetaData.shortestTravelTime}
            trailwaysWebstoreAppURL={props.trailwaysWebstoreAppURL}
            travelDate={props.returnDate}
            trip={'return'}
            type={'round trip'}
          />
        </div>
      )}
    </>
  );
};

export default Schedules;
